import api from './api.service';

/** Mirrors backend/src/core/report-document/report-document.types.ts */
/** `image`: cell value is a public `/uploads/...` URL shown as a thumbnail. */
export type ReportCellKind = 'text' | 'money' | 'number' | 'date' | 'percent' | 'image';
export type ReportCellValue = string | number | null;

export interface ReportColumn {
  key: string;
  label: string;
  kind?: ReportCellKind;
  weight?: number;
  align?: 'left' | 'right' | 'center';
}

export type ReportRowStyle = 'normal' | 'opening' | 'subtotal' | 'total' | 'group' | 'danger' | 'muted';

export interface ReportRow {
  cells: Record<string, ReportCellValue>;
  style?: ReportRowStyle;
  indent?: number;
  span?: number;
  links?: Array<{ path: string; column: string }>;
}

export interface ReportTableSection {
  kind: 'table';
  heading: string;
  subheading?: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  emptyText?: string;
}

export interface ReportAgeingSection {
  kind: 'ageing';
  heading: string;
  subheading?: string;
  basis?: string;
  currency: string;
  buckets: Array<{ label: string; amount: number; overdue?: boolean }>;
  total: number;
}

export interface ReportNotesSection {
  kind: 'notes';
  heading: string;
  lines: string[];
}

export interface ReportKeyValuesSection {
  kind: 'key_values';
  heading: string;
  rows: Array<[string, string]>;
}

export interface ReportTotalsSection {
  kind: 'totals';
  currency: string;
  lines: Array<{ label: string; amount: number; strong?: boolean }>;
  amount_in_words?: string;
}

export interface ReportPartiesSection {
  kind: 'parties';
  heading?: string;
  parties: Array<{ label: string; name: string; lines: string[] }>;
}

export interface ReportTextSection {
  kind: 'text';
  heading: string;
  paragraphs: string[];
  new_page?: boolean;
}

export interface ReportSignaturesSection {
  kind: 'signatures';
  heading: string;
  signers: Array<{ role?: string; name: string; designation?: string | null; signed_at?: string | null; mode?: 'approval' | 'wet'; signature_image_url?: string | null }>;
  acceptance_label?: string;
}

export type ReportSection =
  | ReportTableSection
  | ReportAgeingSection
  | ReportNotesSection
  | ReportKeyValuesSection
  | ReportTotalsSection
  | ReportPartiesSection
  | ReportTextSection
  | ReportSignaturesSection;

export type ReportTileTone = 'default' | 'strong' | 'danger' | 'success';

export interface ReportTile {
  label: string;
  value: ReportCellValue;
  kind?: ReportCellKind;
  currency?: string;
  tone?: ReportTileTone;
}

export interface ReportIntegrityCheck {
  label: string;
  passed: boolean;
  detail?: string;
}

export interface ReportParty {
  type: string;
  id: string | null;
  label: string;
  name: string;
  code?: string;
  email?: string;
  lines: string[];
}

export interface ReportDocument {
  report_key: string;
  title: string;
  subtitle?: string;
  reference: string;
  generated_at: string;
  base_currency: string;
  /** Document language (`en` | `ar`); independent of UI language. */
  language?: string;
  dir?: 'ltr' | 'rtl';
  period: { from?: string | null; to?: string | null; as_of?: string | null };
  orientation: 'portrait' | 'landscape';
  company: { name: string; email: string; phone: string };
  party?: ReportParty;
  meta: Array<[string, string]>;
  filters: Array<[string, string]>;
  tiles: ReportTile[];
  sections: ReportSection[];
  notes: string[];
  show_bank_details: boolean;
  disclaimer?: string;
  watermark?: string;
  company_label?: string;
  integrity: ReportIntegrityCheck[];
  email: {
    to: string[];
    subject: string;
    key_figures: Array<{ label: string; value: string; tone?: ReportTileTone }>;
    include_bank: boolean;
  };
  file_base_name: string;
}

export interface FinancialReportDispatch {
  id: string;
  report_key: string;
  document_ref: string;
  title: string;
  party_type: string | null;
  party_id: string | null;
  party_name: string | null;
  params: Record<string, unknown>;
  recipients: string[];
  cc: string[];
  subject: string;
  status: 'Sent' | 'Failed';
  error_message: string | null;
  sent_by: string | null;
  sent_by_name: string | null;
  sent_at: string;
}

export type ReportParams = Record<string, string | number | boolean | null | undefined>;

export interface SendReportPayload {
  params: ReportParams;
  to: string[];
  cc?: string[];
  subject?: string;
  message?: string;
}

const REPORT_TIMEOUT_MS = 300_000;

function cleanParams(params: ReportParams): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

function unwrap<T>(response: unknown): T {
  const data = (response as any)?.data ?? response;
  return (data?.data ?? data) as T;
}

export async function getReportDocument(key: string, params: ReportParams): Promise<ReportDocument> {
  const response = await api.get(`/finance/report-documents/${key}`, { params: cleanParams(params), timeout: REPORT_TIMEOUT_MS });
  return unwrap<ReportDocument>(response);
}

export async function getReportDocumentFile(
  key: string,
  format: 'pdf' | 'xlsx',
  params: ReportParams,
  options: { preprinted?: boolean } = {}
): Promise<Blob> {
  const res = await api.getClient().get(`/finance/report-documents/${key}/${format}`, {
    params: { ...cleanParams(params), ...(format === 'pdf' && options.preprinted ? { preprinted: '1' } : {}) },
    responseType: 'blob',
    timeout: REPORT_TIMEOUT_MS,
  });
  const type = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  return res.data instanceof Blob ? res.data : new Blob([res.data], { type });
}

export async function sendReportDocument(key: string, payload: SendReportPayload): Promise<FinancialReportDispatch> {
  const response = await api.post(`/finance/report-documents/${key}/email`, { ...payload, params: cleanParams(payload.params) });
  return unwrap<FinancialReportDispatch>(response);
}

export async function getReportDispatches(filter: { key?: string; party_type?: string; party_id?: string; document_id?: string }): Promise<FinancialReportDispatch[]> {
  const response = await api.get('/finance/report-documents/dispatches', { params: cleanParams(filter) });
  return unwrap<FinancialReportDispatch[]>(response) ?? [];
}

export function reportFileName(doc: Pick<ReportDocument, 'file_base_name' | 'reference'> | undefined, key: string, ext: 'pdf' | 'xlsx'): string {
  const base = (doc?.file_base_name || doc?.reference || key).replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 80);
  return `${base}.${ext}`;
}
